<template>
  <form class="space-y-4" @submit.prevent="handleSubmit">
    <div class="rounded-md space-y-3">
      <FormInput
        id="email-address"
        name="email"
        type="email"
        label="Email adresa"
        v-model="formData.email"
        placeholder="Email adresa"
        required
        :error="errors.email"
        inputClass=""
      />

      <FormInput
        id="password"
        name="password"
        type="password"
        label="Lozinka"
        v-model="formData.password"
        placeholder="Lozinka"
        required
        :error="errors.password"
        inputClass=""
      />
    </div>

    <div
      class="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2"
    >
      <FormCheckbox
        id="remember-me"
        name="remember-me"
        v-model="formData.rememberMe"
      >
        <span class="text-sm">Zapamti me</span>
      </FormCheckbox>

      <router-link
        to="/forgot-password"
        class="text-sm font-medium text-indigo-400 hover:text-indigo-300 mt-2 sm:mt-0 text-right"
      >
        Zaboravili ste lozinku?
      </router-link>
    </div>

    <!-- Rate limiting warning -->
    <div
      v-if="rateLimitWarning"
      class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-3 py-2 rounded relative text-sm"
      role="alert"
    >
      <span class="block sm:inline">{{ rateLimitWarning }}</span>
    </div>

    <!-- Server error message -->
    <div
      v-if="serverError"
      class="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded relative text-sm"
      role="alert"
    >
      <span class="block sm:inline">{{ serverError }}</span>
    </div>

    <div class="pt-2">
      <FormButton :loading="loading" :disabled="loginBlocked" class="w-full">
        Prijavi se
      </FormButton>
    </div>

    <!-- Rate limit cooldown timer -->
    <div v-if="cooldownActive" class="text-center text-sm text-red-500">
      Previše pokušaja prijave. Pokušajte ponovo za
      {{ cooldownTimeRemaining }} sekundi.
    </div>
  </form>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import FormInput from './FormInput.vue';
import FormCheckbox from './FormCheckbox.vue';
import FormButton from './FormButton.vue';
import BaseAuthForm from './BaseAuthForm.vue';
import api from '@/services/api';
import securityService from '@/services/security';
import formValidator from '@/services/formValidator';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginErrors {
  email: string;
  password: string;
}

export default defineComponent({
  name: 'LoginForm',
  extends: BaseAuthForm,
  components: {
    FormInput,
    FormCheckbox,
    FormButton,
  },
  data() {
    return {
      formData: {
        email: '',
        password: '',
        rememberMe: false,
      } as LoginFormData,
      errors: {
        email: '',
        password: '',
      } as LoginErrors,
    };
  },
  computed: {
    loginBlocked(): boolean {
      return this.formBlocked; // Use base computed property
    },
  },
  methods: {
    validateForm(): boolean {
      this.errors = {
        email: '',
        password: '',
      };

      let isValid = true;

      // Use the validator service for validations
      const emailValidation = formValidator.validateEmail(this.formData.email);
      if (!emailValidation.isValid) {
        this.errors.email = emailValidation.error;
        isValid = false;
      }

      const passwordValidation = formValidator.validatePassword(
        this.formData.password
      );
      if (!passwordValidation.isValid) {
        this.errors.password = passwordValidation.error;
        isValid = false;
      }

      return isValid;
    },

    async handleSubmit(): Promise<void> {
      if (!this.validateForm()) {
        return;
      }

      // Check rate limiting
      if (!securityService.trackLoginAttempt()) {
        this.startCooldownTimer();
        this.serverError = 'Previše pokušaja prijave. Molimo sačekajte.';
        return;
      }

      // Update rate limit warning
      this.updateRateLimitWarning();

      this.loading = true;
      this.serverError = '';

      try {
        // First ensure CSRF token is available
        await securityService.refreshCsrfToken();

        // Then make the login request
        const response = await api.post('/api/auth/login', {
          email: this.formData.email,
          password: this.formData.password,
          rememberMe: this.formData.rememberMe,
        });

        // Process response using the shared method from BaseAuthForm
        this.processAuthResponse(response.data, 'login-success');
      } catch (error) {
        // Use the shared error handler from BaseAuthForm
        this.handleApiError(error, 'login');
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    // Check if we should fill in remembered email
    const rememberedEmail = localStorage.getItem('email');
    const rememberMe = localStorage.getItem('rememberMe') === 'true';

    if (rememberedEmail && rememberMe) {
      // Sanitize remembered email for extra safety
      this.formData.email = securityService.sanitizeInput(rememberedEmail);
      this.formData.rememberMe = true;
    }

    // Ensure CSRF token is available when component mounts
    securityService.refreshCsrfToken();
  },
});
</script>
